// bindColonyMessages(store) — inbound message router (colony-screen design D2/D12,
// extended by match-screen D-sum-4; client-colony "Colony store slice and inbound
// message routing"; client-wire "The inbound router routes the Result message"). The
// session owner's `Connection.onServerMessage` callback (already decoding every
// steady-state server message — `connection.ts` `STEADY_TYPES`, plus `welcome`) hands
// each message here; this router forwards the colony-relevant messages into the
// colony slice's actions, routes a decoded `result` into `applyMatchResult`, applies
// `welcome`'s own-colony identity into `SessionSlice.colonyId` and persists its
// `manifest` (the one production writer of both fields — no other caller resolves
// them, so `selectOwnScoreboardRow`/`selectOwnEliminated` returned null/false in
// production until this was wired; implementation-review F2), and is a no-op for
// everything else. `keyframe`/`delta` never reach `onServerMessage` at all (D8 routes
// them through `onWorld` instead), and a mutation `Rejected` is surfaced via the
// separate `onMutationRejected` callback, not this one.
//
// The UI invents no data: every field this router touches traces to a decoded
// message, via the store's own `applyColonyMeta`/`applyPromptStatus`/
// `applyStateHistogram`/`applyMatchState`/`applyMatchResult`/`setManifest` actions
// (state.ts) — this module holds no state of its own.

import { useClientStore, type ClientStore } from '../../state/store';
import type { ServerMessage } from '../../net/wire/messages';

/**
 * Builds the `onServerMessage` handler to wire into the session owner's
 * `Connection` callbacks. `store` is a `ClientStore` snapshot (e.g.
 * `useClientStore.getState()`) — its actions are stable closures over the live
 * store regardless of when the snapshot was taken, the same convention
 * `InMatchCanvas` already relies on for its own callbacks.
 */
export function bindColonyMessages(store: ClientStore): (msg: ServerMessage) => void {
  return (msg: ServerMessage): void => {
    switch (msg.type) {
      case 'colonyMeta':
        store.applyColonyMeta(msg.value);
        return;
      case 'promptStatus':
        store.applyPromptStatus(msg.value);
        return;
      case 'stateHistogram':
        store.applyStateHistogram(msg.value);
        return;
      case 'matchState':
        store.applyMatchState(msg.value);
        return;
      case 'result':
        store.applyMatchResult(msg.value);
        return;
      case 'welcome': {
        // Role reconciliation, monotonic toward spectator (design DD7 / D-083). A null
        // own colony is always a spectator; otherwise the session is a player UNLESS it
        // is already recorded as a spectator — a `Welcome` never re-promotes a spectator
        // (the server retains an eliminated session's colony binding, so a reconnect
        // `Welcome` can carry a non-null colony for a spectator). `currentRole` MUST be a
        // *live* read of the store role at message time, never the `role` field of the
        // `getState()` snapshot this router closed over at bind time (W5-F1): a mid-match
        // elimination flips the live role to spectator via `applyMatchState` *after* the
        // bind snapshot was captured, and a stale-snapshot read would re-promote a
        // transient-reconnect `Welcome` back to 'player' until the next `MatchState`.
        const currentRole = useClientStore.getState().role;
        const role =
          msg.value.yourColony === null
            ? 'spectator'
            : currentRole === 'spectator'
              ? 'spectator'
              : 'player';
        // `ownHive` is the colony play view's camera anchor (`colony-field-canvas`): a
        // `[hx, hy]` for a bound session, `null` for a spectator — written here alongside
        // `colonyId`/`manifest`, cleared by the store's `reset()`, never fabricated.
        store.setSession({ colonyId: msg.value.yourColony, role, ownHive: msg.value.yourHive });
        store.setManifest(msg.value.manifest);
        return;
      }
      default:
        return; // keyframe/delta/rejected: out of this router's scope
    }
  };
}
